import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { setQueryParams } from '../../store/slices/removebgLogs';
import { FilterBar, useFilterBarStyles } from '../ui';
import { dateParam } from '../../lib/orderFilters';

const resolutionValues = [
    {label: 'All', value: ''},
    {label: '512x512', value: '512'},
    {label: '1024x1024', value: '1024'},
]

const retryValues = [
    {label: 'All', value: ''},
    {label: 'With Retry', value: 'true'},
    {label: 'Without Retry', value: 'false'},
]


/** Filters of the remove.bg log list; every change updates the store and reloads page 1. */
export default function RemovebgLogsFilters(props: any) {
    const { queryParams, loadLogs, loading } = props;
    const dispatch = useDispatch();
    const classes = useFilterBarStyles();

    const handleFilterChange = (e) => {
        const { name, value } = e.target;

        const newQueryParams = {
            ...queryParams,
            page: 1,
            [name]: value,
        }

        if(name === 'maxDate'){
            if(!newQueryParams.minDate){
                // If no min date is set, set it to the same as max date
                newQueryParams.minDate = value;
            } else{
                const maxDateObj = dayjs(newQueryParams.maxDate);
                const minDateObj = dayjs(newQueryParams.minDate);
                // If max date is earlier than min date, automatically set min date to max date
                if(maxDateObj.isBefore(minDateObj)){
                    newQueryParams.minDate = newQueryParams.maxDate;
                }
            }
        }
        if(name === 'minDate' && !newQueryParams.maxDate){
            // If setting min date and no max date is set, set max date to today
            newQueryParams.maxDate = Dayjs(new Date()).format("YYYY-MM-DD")
        }

        // Immediately update the store with new queryParams
        dispatch(setQueryParams(newQueryParams));
        
        // Then load the data
        loadLogs(newQueryParams);
    };

    const clearFilters = () => {
        const defaultParams = {
            ...queryParams,
            page: 1,
            status: '',
            targetResolution: '',
            isRetryExist: '',
            minDate: dayjs().format('YYYY-MM-DD'),
            maxDate: dayjs().format('YYYY-MM-DD'),
            eventId: '',
            customerId: '',
            log_like: '',
        };
        // Immediately update the store with cleared queryParams
        dispatch(setQueryParams(defaultParams));
        
        // Then load the data
        loadLogs(defaultParams);
    };

    const onDate = (name: 'minDate' | 'maxDate') => (value: Dayjs.Dayjs | null) => {
        const iso = dateParam(value);
        if (iso) handleFilterChange({ target: { name, value: iso } });
    };

    return (
        <FilterBar label="Remove background log filters">
            <FormControl className={classes.select} size="small">
                <InputLabel id="resolution-select-label">Resolution</InputLabel>
                <Select
                    labelId="resolution-select-label"
                    value={queryParams?.targetResolution || ''}
                    label="Resolution"
                    name="targetResolution"
                    onChange={(event) => handleFilterChange(event)}
                    disabled={loading}
                >
                    {resolutionValues.map((item) => <MenuItem key={item.label} value={item.value}>{item.label}</MenuItem>)}
                </Select>
            </FormControl>
            <FormControl className={classes.select} size="small">
                <InputLabel id="retry-select-label">Retry</InputLabel>
                <Select
                    labelId="retry-select-label"
                    value={queryParams?.isRetryExist || ''}
                    label="Retry"
                    name="isRetryExist"
                    onChange={(event) => handleFilterChange(event)}
                    disabled={loading}
                >
                    {retryValues.map((item) => <MenuItem key={item.label} value={item.value}>{item.label}</MenuItem>)}
                </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    className={classes.date}
                    label="From"
                    format="YYYY-MM-DD"
                    value={queryParams?.minDate ? dayjs(queryParams.minDate) : dayjs()}
                    maxDate={queryParams?.maxDate ? dayjs(queryParams.maxDate) : dayjs()}
                    onChange={onDate('minDate')}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                    className={classes.date}
                    label="To"
                    format="YYYY-MM-DD"
                    value={queryParams?.maxDate ? dayjs(queryParams.maxDate) : dayjs()}
                    maxDate={dayjs()}
                    onChange={onDate('maxDate')}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small' } }}
                />
            </LocalizationProvider>
            <TextField
                className={classes.text}
                label="Event ID"
                size="small"
                name="eventId"
                value={queryParams?.eventId || ''}
                onChange={(event) => handleFilterChange(event)}
                disabled={loading}
            />
            <TextField
                className={classes.text}
                label="Customer ID"
                size="small"
                name="customerId"
                value={queryParams?.customerId || ''}
                onChange={(event) => handleFilterChange(event)}
                disabled={loading}
            />
            <FormControl className={classes.select} size="small">
                <InputLabel id="sort-direction-label">Sort</InputLabel>
                <Select
                    labelId="sort-direction-label"
                    value={queryParams?.sortDirection || 'DESC'}
                    label="Sort"
                    name="sortDirection"
                    onChange={(event) => handleFilterChange(event)}
                    disabled={loading}
                >
                    <MenuItem value="DESC">Newest first</MenuItem>
                    <MenuItem value="ASC">Oldest first</MenuItem>
                </Select>
            </FormControl>
            <Button className={classes.clear} variant="text" size="small" onClick={clearFilters} disabled={loading}>
                Clear filters
            </Button>
        </FilterBar>
    );
}
